/**
 * The provenance model: entries as the screens render them.
 *
 * This file maps items — drafts and recorded entries alike — to rows, cards,
 * headers and payloads. The three concerns it composes live beside it and are
 * re-exported here so no importer has to know the split: editing a draft
 * (`provenance.draft.ts`), dates (`provenance.dates.ts`) and verification
 * (`provenance.verification.ts`).
 */
import { isContested, isDefined } from "@ondo/shared";
import type {
  AddProvenanceEntryRequest,
  ProvenanceDocument,
  ProvenanceDocumentKind,
  ProvenanceEntry,
  ProvenanceEntryKind,
  ProvenanceEvidenceClaim,
  ProvenanceMethod,
  ProvenanceVerificationRequest,
} from "@ondo/shared";
import { formatMoney } from "@ondo/shared";

import { describeBytes } from "../lib/files.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import { plural } from "../lib/text.ts";
import {
  ACQUISITION_METHODS,
  DOCUMENT_KINDS,
  EVIDENCE_GRADES,
  HISTORY_KINDS,
  HISTORY_KIND_ORDER,
  LOAN_ACT_EVENTS,
  METHOD_EVENTS,
  PROVENANCE_COPY,
  STANDING_PILLS,
} from "./provenance.constants.ts";
import type { EventTone, HistoryTextKey } from "./provenance.constants.ts";
import { describeDateRange } from "./provenance.dates.ts";
import { needsClaimedArtist, strip } from "./provenance.draft.ts";
import type { ClaimedArtist, DraftEntry, ProvenanceItem } from "./provenance.draft.ts";
import type { SurfaceConfig } from "./provenance.surfaces.ts";
import {
  askedFor,
  confirmationStrip,
  panelFor,
  toConfirmationDetail,
  withdrawalFor,
} from "./provenance.verification.ts";
import type {
  ConfirmationDetail,
  ConfirmationLoad,
  ConfirmationStrip,
  EntryRemoval,
  VerificationModel,
  VerificationPanel,
} from "./provenance.verification.ts";

export * from "./provenance.dates.ts";
export * from "./provenance.draft.ts";
export * from "./provenance.verification.ts";

export interface EvidencePillModel {
  /** 'VERIFIED FACT'. Uppercase in the constant, not in CSS. */
  label: string;
  /** Visually hidden, so the pill is announced as an assertion. */
  qualifier: string;
  /** 'var(--grade-documented)' — a token name, as `accentFor` returns one. */
  token: string;
  bgToken: string;
}

/**
 * Where a document's bytes are.
 *
 * A union so a file picked on this device and a row in the bucket are the same
 * kind of thing to whoever wants to open one. `contentType` is on both branches
 * because it is what decides how the thing renders — never the filename, which
 * a collector controls and which lies about as often as not.
 */
export type DocumentSource =
  | { kind: "stored"; objectPath: string; contentType: string }
  | { kind: "local"; file: File; contentType: string };

export interface DocumentRow {
  id: string;
  name: string;
  kind: ProvenanceDocumentKind;
  kindLabel: string;
  sizeLabel: string;
  /** False once somebody has staked a name on the entry: evidence under an
   * attestation is part of what they were shown. */
  removable: boolean;
  source: DocumentSource;
  /** 'Open Private sale agreement' — the accessible name of the control. */
  openLabel: string;
  /**
   * What the record holds about THIS paper, and what this reader may do to it.
   *
   * Null on a draft's document, which has not been filed and so has nothing
   * said about it and nowhere to say it — the same reason a draft entry has no
   * standing to state.
   */
  verdict: DocumentVerdictModel | null;
}

/**
 * The accept/reject control on one document — OND-62.
 *
 * ONE SHAPE CARRYING BOTH THE STATE AND THE CONTROLS, which is
 * `VerificationPanel`'s arrangement one level down and for its reason: a
 * component that received a count, a stance and an `editable` boolean would
 * have to decide what to render from them, and that decision is a rule.
 *
 * `mine` is the reader's own live position or null, and it is what the two
 * buttons render as pressed against. It is NOT derived from the counts: a paper
 * with one acceptance tells the reader nothing about whether the acceptance was
 * theirs.
 */
export interface DocumentVerdictModel {
  acceptedBy: number;
  rejectedBy: number;
  /** The reader's own live verdict, if they hold one. */
  mine: "verify" | "dispute" | null;
  /**
   * The id of that verdict, so Undo has something to name.
   *
   * Non-null exactly when `mine` is, because the server sets both from the same
   * row — read together for `respondedBy`'s reason, so "I hold a position with
   * no id" cannot be expressed here.
   */
  mineId: string | null;
  /**
   * What the counts say, in words: 'Accepted by 2 · Rejected by 1'.
   *
   * Empty when nobody has judged it, rather than 'Accepted by 0 · Rejected by
   * 0' — a paper nobody has looked at should read as nothing said, not as a
   * score of nil-nil.
   */
  summary: string;
  /**
   * Whether this reader may take a position at all.
   *
   * The server's answer, not a guess: reading the paper is what entitles you to
   * judge it, and a reader who cannot see the document never receives the row.
   * False on a surface that only reads.
   */
  canJudge: boolean;
}

/**
 * What identifies one link, as against what it says.
 *
 * Everything a reader sees is on the card; this is the ordinal and the name
 * behind it — the toggle's accessible name, and the label on the button that
 * saves a draft. Kept apart from `EntryCardModel` because it survives a
 * redesign of the card and the card does not survive a renumbering.
 */
export interface EntryHeaderModel {
  /**
   * 1-based position in the RECORD, not in what is on screen.
   *
   * Renumbers when a live entry above it is removed or withdrawn. Does NOT
   * renumber when a tab narrows the list to one kind — the work record's
   * Exhibitions tab is a filter over the one history, so a row that is the ninth
   * of fourteen is the ninth of fourteen wherever it is read. See `toEntryRows`.
   */
  index: number;
  /** 'Entry 2 of 4: Galerie Krinzinger' — the toggle's accessible name. */
  label: string;
  ownerName: string;
}

export interface FieldRow {
  label: string;
  value: string;
}

/** The green line under an owner field. Null until an id has been attached. */
export interface OndoLine {
  ondoId: string;
  note: string;
}

export interface EntryFormModel {
  values: DraftEntry;
  /** The green line, or null. Present only when an id was attached. */
  ondo: OndoLine | null;
  documents: DocumentRow[];
  verification: VerificationPanel;
  /** Always present: a draft is in this browser and nowhere else, so it is
   *  always its author's to drop. */
  removal: EntryRemoval;
}

export interface EntryReadModel {
  fields: FieldRow[];
  ondo: { ondoId: string; note: string } | null;
  documents: DocumentRow[];
  verification: VerificationPanel;
  /** Who has put their name to this link, and what they said. */
  confirmations: ConfirmationDetail;
  /** Null when this reader may not withdraw, or the surface does not offer it. */
  removal: EntryRemoval | null;
  /**
   * HOW THIS ENTRY IS CONTESTED — OND-62. Null when nothing is.
   *
   * Null rather than an `uncontested` model, because the ordinary case must
   * render nothing at all: an entry nobody has looked at is uncontested, and a
   * grey "UNCONTESTED" pill on every row would read as a finding the registry
   * has not made. So the view has nothing to suppress and no condition to get
   * wrong.
   */
  standing: StandingModel | null;
}

/**
 * The contested state of one entry, as a pill and a sentence.
 *
 * `EvidencePillModel`'s shape, and the distinction between the two is worth
 * keeping: that one is what the AUTHOR says about their own evidence, this is
 * what OTHER PEOPLE have put on the record against the claim. They sit on the
 * same card and must not be mistaken for each other, which is why the sentence
 * comes with the pill rather than being left to a tooltip.
 */
export interface StandingModel {
  /** 'QUESTIONABLE'. Uppercase in the constant, not in CSS. */
  label: string;
  /** Why it reads that way, in one sentence. */
  sentence: string;
  token: string;
  bgToken: string;
  /**
   * 'One of four documents rejected' — or null when the standing is not about
   * the documents at all, which is every disputed entry.
   */
  documentNote: string | null;
}

export type EntryRow =
  | {
      state: "draft";
      id: string;
      header: EntryHeaderModel;
      card: EntryCardModel;
      form: EntryFormModel;
    }
  | {
      state: "recorded";
      id: string;
      header: EntryHeaderModel;
      card: EntryCardModel;
      read: EntryReadModel;
    };

/**
 * One link, as the event card at the top of it.
 *
 * Three signals that are deliberately not merged: `action` and `typeLabel` say
 * WHAT HAPPENED, `pill` says how good the author thinks their own evidence is,
 * and `strip` says who has put their name to it. Any two of them collapsed into
 * one badge would have the card asserting something no part of the registry
 * knows.
 */
export interface EntryCardModel {
  /** 'Acquired' — the act, read off the method. */
  action: string;
  /** 'PURCHASE' — the method itself, beside the act. */
  typeLabel: string;
  tone: EventTone;
  glyph: string;
  /** '2003', '1979–2001', 'Date unknown'. Right-aligned. */
  dates: string;
  ownerName: string;
  /** 'Galerie Krinzinger · Vienna', or '' when neither is recorded. */
  detail: string;
  notes: string | null;
  pill: EvidencePillModel;
  documentCount: string;
  strip: ConfirmationStrip;
}

export interface TimelineRow {
  id: string;
  ownerName: string;
  /** 'Galerie Krinzinger, Vienna · Vienna, Austria · Purchase'. */
  subLine: string;
  pill: EvidencePillModel;
  documentCount: string;
  /** '✓ Verified' when at least one live attestation exists. Null otherwise. */
  attested: string | null;
  /** '1979–2001', right-aligned. */
  dates: string;
}

/**
 * Which field on a recorded entry each form key reads back from.
 *
 * The draft and the API disagree on names by design — `owner` is what a form
 * calls it and `ownerName` is what the record calls it — and this is the one
 * place that translation lives. Everything else reads through `textOf`.
 */
const SERVER_TEXT: Record<HistoryTextKey, keyof ProvenanceEntry> = {
  owner: "ownerName",
  counterparty: "counterpartyName",
  venue: "venueName",
  title: "titleText",
  reference: "referenceText",
  person: "personName",
  publisher: "publisherText",
  price: "priceText",
  gallery: "galleryText",
  location: "locationText",
  insurer: "insurerName",
  policy: "policyReference",
  // The one key the two sides agree on, because the server's own name for it is
  // already what the claim is: there is no shorter word for 'the artist this
  // entry claims the work is by' that a form would use instead.
  claimedArtistName: "claimedArtistName",
};

/** One field, whichever side of the wire the entry is on. Trimmed, never null. */
function textOf(entry: DraftEntry | ProvenanceEntry, key: HistoryTextKey): string {
  // `grade` is the draft's word and `evidenceClaim` is the record's, which makes
  // it the discriminator. Both now carry `kind`, so that no longer is one.
  if ("grade" in entry) return entry[key].trim();

  const value = entry[SERVER_TEXT[key]];
  return typeof value === "string" ? value.trim() : "";
}

function kindOf(entry: DraftEntry | ProvenanceEntry): ProvenanceEntryKind {
  return entry.kind;
}

/**
 * The name this entry goes by — the venue for an exhibition, the house for an
 * auction, the holder for a sale, the conservator for a condition report.
 *
 * One line rather than eight, because every kind has exactly one field that
 * answers "what is this entry about" and `HISTORY_KINDS.headline` names it.
 */
function entryHeadline(entry: DraftEntry | ProvenanceEntry): string {
  return textOf(entry, HISTORY_KINDS[kindOf(entry)].headline);
}

/**
 * Everything else the kind carries, in the order it carries it.
 *
 * 'Galerie Pierre · Paris · Cat. no. 7'. Built from the same table the form
 * renders, so a field added to a kind appears on its card without a second
 * decision — and an empty one simply is not there.
 */
function entryDetail(entry: DraftEntry | ProvenanceEntry): string {
  const model = HISTORY_KINDS[kindOf(entry)];

  return model.fields
    .filter((field) => field.control === "text" && field.key !== model.headline)
    .map((field) => (field.control === "text" ? textOf(entry, field.key) : ""))
    .filter((part) => part !== "")
    .join(" · ");
}

export function gradePill(grade: ProvenanceEvidenceClaim): EvidencePillModel {
  const found = EVIDENCE_GRADES.find((option) => option.key === grade);
  const chosen = found ?? EVIDENCE_GRADES[EVIDENCE_GRADES.length - 1];

  return {
    label: chosen?.pill ?? "",
    qualifier: PROVENANCE_COPY.gradeQualifier,
    token: chosen?.token ?? "var(--grade-unverified)",
    bgToken: chosen?.bgToken ?? "var(--grade-unverified-bg)",
  };
}

/** 'Galerie Krinzinger, Vienna · Vienna, Austria · Purchase'. */
export function timelineSubLine(entry: DraftEntry | ProvenanceEntry): string {
  const detail = entryDetail(entry);
  const model = HISTORY_KINDS[kindOf(entry)];
  const suffix = entry.kind === "sale" ? methodLabel(entry.method) : model.chip;

  return [detail, suffix].filter((part) => part !== "").join(" · ");
}

export function countDocuments(count: number): string {
  if (count === 0) return PROVENANCE_COPY.noDocuments;
  return count === 1 ? "1 doc" : `${String(count)} docs`;
}

/**
 * Every live entry, as the surface named by the caller shows it.
 *
 * The surface is threaded here and nowhere else. No component receives it, so
 * none of them can branch on which screen it is on — the whole difference
 * between composing a chain of title and reading one is already decided by the
 * time a row reaches a view.
 */
export function toEntryRows(
  items: ProvenanceItem[],
  surface: SurfaceConfig,
  loaded: {
    confirmations?: Record<string, ConfirmationLoad>;
    /** Outstanding asks by entry id, once they have been fetched. */
    asks?: Record<string, ProvenanceVerificationRequest[]>;
  } = {},
  /**
   * Which of the numbered rows to return. Everything, by default.
   *
   * NUMBERING HAPPENS BEFORE THIS, and that is the whole reason the argument
   * exists rather than the caller filtering its own array. The work record's
   * Exhibitions, Documents and Condition tabs are filters over the one history
   * — see docs/artworkHistory.md — so a row's ordinal has to say where it sits
   * in the record, not where it sits in the slice somebody is looking at.
   * Narrowing the array before it arrives here renumbers it, and the same entry
   * reads 'Entry 1 of 2' on Documents and 'Entry 9 of 14' on Artwork history:
   * two different claims about one row.
   *
   * The ordinal is an index among LIVE rows and never `entry.position`. Drafts
   * carry no position at all, a withdrawn row keeps its position while `isLive`
   * drops it, and `provenance_chain` is bounded by readable entries — so a
   * verifier holding one grant would be shown 'Entry 13 of 1'.
   */
  only?: (item: ProvenanceItem) => boolean,
): EntryRow[] {
  const live = items.filter(isLive);

  return live
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => only === undefined || only(item))
    .map(({ item, index }) =>
      toEntryRow({
        item,
        index,
        total: live.length,
        surface,
        confirmations: loaded.confirmations?.[item.entry.id],
        asks: loaded.asks?.[item.entry.id],
      }),
    );
}

function toEntryRow(input: {
  item: ProvenanceItem;
  index: number;
  total: number;
  surface: SurfaceConfig;
  confirmations?: ConfirmationLoad | undefined;
  asks?: ProvenanceVerificationRequest[] | undefined;
}): EntryRow {
  const { item, surface } = input;
  const header = toHeader(item, input.index, input.total);
  const card = toCard(item);

  if (item.kind === "draft") {
    return {
      state: "draft",
      id: item.entry.id,
      header,
      card,
      form: {
        values: item.entry,
        ondo: toOndoLine(item.entry.ondoId),
        documents: item.entry.documents.map((document) => ({
          id: document.id,
          name: document.file.name,
          kind: document.kind,
          kindLabel: documentKindLabel(document.kind),
          sizeLabel: document.sizeLabel,
          removable: true,
          source: { kind: "local", file: document.file, contentType: document.file.type },
          openLabel: `${PROVENANCE_COPY.openDocument} ${document.file.name}`,
          // A file on this device. There is no row to hang a verdict off and
          // nobody could have taken a position on something unsent, which is
          // the same reason a draft entry states no standing.
          verdict: null,
        })),
        verification: panelFor(item, surface.draft, null),
        removal: { label: PROVENANCE_COPY.removeEntry, note: null },
      },
    };
  }

  const asked = surface.asks === "outstanding" ? askedFor(item.entry, input.asks) : null;

  return {
    state: "recorded",
    id: item.entry.id,
    header,
    card,
    read: {
      confirmations: toConfirmationDetail(input.confirmations),
      fields: toFieldRows(item.entry),
      // A recorded entry knows THAT an id was attached and never which — the
      // read shape projects a boolean, so there is nothing here to render.
      ondo: item.entry.ownerOnOndo ? { ondoId: "", note: PROVENANCE_COPY.onOndo } : null,
      documents: item.documents.map((document) => {
        const name = document.label ?? documentKindLabel(document.kind);

        return {
          id: document.id,
          name,
          kind: document.kind,
          kindLabel: documentKindLabel(document.kind),
          sizeLabel: describeBytes(document.byteSize),
          // Removable until somebody has staked a name on the entry. After that
          // it is part of what they were shown. A DRAFT's documents stay removable
          // even once its author has verified it — both are unsent, and they go
          // together or not at all.
          removable:
            surface.documents === "edit" &&
            item.entry.canCurate &&
            item.entry.verifiedByCount + item.entry.disputedByCount === 0,
          source: {
            kind: "stored",
            objectPath: document.objectPath,
            contentType: document.contentType,
          },
          openLabel: `${PROVENANCE_COPY.openDocument} ${name}`,
          // OND-62. The surface decides whether a verdict is offered at all;
          // the server decides whether THIS reader may give one. Both have to
          // agree, and neither is consulted twice.
          verdict: documentVerdictFor(
            document,
            surface.verdicts === "offer" && item.entry.canAttest,
          ),
        };
      }),
      verification: panelFor(item, surface.recorded, asked),
      removal: withdrawalFor(item.entry, surface),
      standing: standingFor(item.entry),
    },
  };
}

/**
 * Which documents a click opens, and which of them it opens on.
 *
 * The whole entry rather than the one clicked, so paging inside the viewer
 * works — and it costs nothing, because signing a set is one round trip
 * whichever way. Null for an id this chain does not hold, which is what a stale
 * click after a re-read looks like.
 */
export function documentsToOpen(
  items: ProvenanceItem[],
  surface: SurfaceConfig,
  entryId: string,
  documentId: string,
): { documents: DocumentRow[]; index: number } | null {
  const row = toEntryRows(items, surface).find((entry) => entry.id === entryId);
  if (!row) return null;

  const documents = row.state === "draft" ? row.form.documents : row.read.documents;
  const index = documents.findIndex((document) => document.id === documentId);

  return index === -1 ? null : { documents, index };
}

export function toTimelineRows(items: ProvenanceItem[]): TimelineRow[] {
  return items.filter(isLive).map((item) => {
    const entry: DraftEntry | ProvenanceEntry = item.entry;
    const grade = item.kind === "draft" ? item.entry.grade : item.entry.evidenceClaim;
    const attested = verifiedCount(item) > 0 ? PROVENANCE_COPY.verifiedMark : null;

    return {
      id: entryId(item),
      ownerName: entryOwner(item),
      subLine: timelineSubLine(entry),
      pill: gradePill(grade),
      documentCount: countDocuments(entryDocumentCount(item)),
      attested,
      dates: entryDates(entry),
    };
  });
}

/**
 * The Add-to-artwork-history modal, as one value.
 *
 * Pure, and it takes the draft rather than a list, because the modal composes
 * exactly one entry — there is no index, no position and no neighbouring row for
 * it to reason about. `form` null is the state the design does not draw: open,
 * with no chip chosen, and the fields not yet decided.
 */
/**
 * One chip in the modal's row, already decided.
 *
 * A view-model rather than the component reading `HISTORY_KINDS` itself, which
 * is what it used to do. The reason is the ninth kind: whether a chip may be
 * pressed is now a rule about the WORK — a claim about authorship needs an
 * artist the registry holds — and a rule is not something a component gets to
 * evaluate. The component maps over these and renders what it is given.
 */
export interface HistoryKindChipModel {
  kind: ProvenanceEntryKind;
  /** 'Artist attribution'. */
  label: string;
  glyph: string;
  tone: EventTone;
  chosen: boolean;
  /**
   * NULL MEANS PRESSABLE. A sentence means the chip is offered, disabled, with
   * that sentence as the reason — never hidden.
   *
   * The same rule the Sell control and "+ Add history" follow: a control that
   * is simply absent cannot be told apart from a feature that does not exist,
   * and here the difference matters more than usual, because the thing missing
   * is fixable by the collector.
   */
  unavailable: string | null;
}

/**
 * The chip row, in the design's order, for one work.
 *
 * NO `switch (kind)`: the gate is `needsClaimedArtist`, which asks the field
 * table whether the kind carries a `fixed` field, and the sentence is the
 * kind's own `needsFixedValue`. A tenth kind with a fixed field is gated the
 * day it is added to `HISTORY_KINDS` and this function is not touched.
 */
export function historyKindChips(input: {
  chosen: ProvenanceEntryKind | null;
  /** The work's artist, or null when the record only holds a typed name. */
  claim: ClaimedArtist | null;
}): HistoryKindChipModel[] {
  return HISTORY_KIND_ORDER.map((kind) => {
    const model = HISTORY_KINDS[kind];
    const blocked = needsClaimedArtist(kind) && input.claim === null;

    return {
      kind,
      label: model.chip,
      glyph: model.glyph,
      tone: model.tone,
      chosen: kind === input.chosen,
      unavailable: blocked ? (model.needsFixedValue ?? null) : null,
    };
  });
}

export interface AddHistoryView {
  open: boolean;
  /** 'Sunflower Seeds (Vessel) · Ai Weiwei'. Null until the record has loaded. */
  subject: string | null;
  /** The nine chips, with the reason for any that cannot be pressed. */
  chips: HistoryKindChipModel[];
  kind: ProvenanceEntryKind | null;
  form: EntryFormModel | null;
  /** Verify and ask, on the `dialog` surface. Null before a kind is chosen. */
  acts: VerificationModel | null;
  refusals: string[];
  /** A missing required field, or a refusal from the last attempt to save. */
  problem: string | null;
  busy: boolean;
  /** The one-way door, said before it is walked through. */
  finalNote: string;
}

export function toAddHistoryView(input: {
  open: boolean;
  subject: string | null;
  draft: DraftEntry | null;
  surface: SurfaceConfig;
  refusals: string[];
  problem: string | null;
  busy: boolean;
  finalNote: string;
  /**
   * The work's artist, for the one kind that is read off the record.
   *
   * Optional so every existing caller and test is unchanged, and the absent
   * case is the honest one rather than a convenience: a surface that has not
   * supplied a claim has not got one, and the chip is withheld with its reason.
   */
  claim?: ClaimedArtist | null;
}): AddHistoryView {
  const chips = historyKindChips({
    chosen: input.draft?.kind ?? null,
    claim: input.claim ?? null,
  });

  if (input.draft === null) {
    return {
      open: input.open,
      subject: input.subject,
      chips,
      kind: null,
      form: null,
      acts: null,
      refusals: input.refusals,
      problem: input.problem,
      busy: input.busy,
      finalNote: input.finalNote,
    };
  }

  // One row, through the same builder the list uses, so the modal cannot drift
  // from the form on the register wizard: the fields, the documents and the acts
  // are all decided in one place and narrowed by the surface.
  const [row] = toEntryRows([{ kind: "draft", entry: input.draft }], input.surface);
  const form = row?.state === "draft" ? row.form : null;

  return {
    open: input.open,
    subject: input.subject,
    chips,
    kind: input.draft.kind,
    form,
    acts: form?.verification.acts ?? null,
    refusals: input.refusals,
    problem: input.problem,
    busy: input.busy,
    finalNote: input.finalNote,
  };
}

/** The drafts, in the shape the API takes. Recorded entries are already sent. */
export function toEntryPayloads(
  items: ProvenanceItem[],
  workId: string,
): { localId: string; body: AddProvenanceEntryRequest }[] {
  return items.flatMap((item) =>
    item.kind === "draft"
      ? [{ localId: item.entry.id, body: toEntryPayload(item.entry, workId) }]
      : [],
  );
}

/**
 * A draft as the request the API takes, carrying ONLY what its kind carries.
 *
 * `undefined` for a field this kind does not have, which `JSON.stringify` drops
 * — so a name typed under a chip and then abandoned for another chip never
 * leaves the browser. That matters more than it sounds: `addProvenanceEntrySchema`
 * is `.strict()` and would refuse the whole entry rather than ignore the stray
 * field, so this is what makes switching chips mid-form safe rather than a
 *400 the collector cannot explain.
 *
 * `endText` is null rather than absent on a point kind. It is on every member of
 * the union, so sending it is legal; sending the draft's stale end date would
 * put a closing date on an auction.
 */
export function toEntryPayload(
  entry: DraftEntry,
  workId: string,
): AddProvenanceEntryRequest {
  const model = HISTORY_KINDS[entry.kind];
  const has = (key: HistoryTextKey) =>
    model.fields.some((field) => field.control === "text" && field.key === key);
  const text = (key: HistoryTextKey) =>
    has(key) ? blankToNull(entry[key]) : undefined;
  const hasMoney = model.fields.some((field) => field.control === "money");

  return {
    workId,
    kind: entry.kind,
    ownerName: text("owner"),
    counterpartyName: text("counterparty"),
    venueName: text("venue"),
    titleText: text("title"),
    referenceText: text("reference"),
    personName: text("person"),
    publisherText: text("publisher"),
    priceText: text("price"),
    galleryText: text("gallery"),
    locationText: text("location"),
    insurerName: text("insurer"),
    policyReference: text("policy"),
    // PARSED HERE AND NOT IN THE CONTROLLER, so the one place that knows the
    // draft holds text and the wire holds minor units is the one place that
    // translates. `validateEntry` has already refused a figure that does not
    // parse, so a null here means the collector left it blank.
    insuredMinor: hasMoney ? parseMoneyMinor(entry.insuredAmount) : undefined,
    insuredCurrency: hasMoney
      ? blankToNull(entry.insuredCurrency.trim().toUpperCase())
      : undefined,
    /**
     * THE TWO HALVES OF THE CLAIM, SENT TOGETHER OR NOT AT ALL.
     *
     * Gated on the kind carrying the claim's FIELD, which is the same `has()`
     * every text field above is gated on — so this is still the field table
     * deciding rather than a branch on `kind`. Both are `string | undefined`
     * rather than nullable: the API's members for the other eight kinds refuse
     * these outright, and `undefined` is what `JSON.stringify` drops.
     *
     * `claimedArtist` is read off the draft's own id rather than from `text()`,
     * because it is not a field — there is no control for it and nothing on
     * screen renders it. See `DraftEntry.claimedArtistId`.
     */
    claimedArtist: has("claimedArtistName")
      ? (entry.claimedArtistId ?? undefined)
      : undefined,
    claimedArtistName: has("claimedArtistName")
      ? (blankToNull(entry.claimedArtistName) ?? undefined)
      : undefined,
    method: model.fields.some((field) => field.control === "method")
      ? entry.method
      : undefined,
    documentKind: model.fields.some((field) => field.control === "documentKind")
      ? entry.documentKind
      : undefined,
    startText: blankToNull(entry.startDate),
    endText: model.dates === "range" ? blankToNull(entry.endDate) : null,
    notes: blankToNull(entry.notes),
    evidenceClaim: entry.grade,
  };
}

/**
 * Server entries as list items, in the order the server gave them.
 *
 * Which is `position, created_at` ascending — oldest first, a chain read the way
 * a chain is written. No sort here, deliberately: two orderings of one list is
 * one more than can be right, and `provenance_chain` is the one that knows.
 */
export function toRecordedItems(
  entries: ProvenanceEntry[],
  documents: Record<string, ProvenanceDocument[]>,
): ProvenanceItem[] {
  return entries.map((entry) => ({
    kind: "recorded",
    entry,
    documents: documents[entry.id] ?? [],
  }));
}

/**
 * '3 entries · 2 with documents', or 'None recorded'.
 *
 * Names the documents because "3 entries, none documented" and "3 entries, all
 * documented" are materially different submissions, and the confirm step is the
 * last place anyone will notice which one they are sending.
 */
export function describeProvenance(items: ProvenanceItem[]): string {
  const live = items.filter(isLive);
  if (live.length === 0) return "None recorded";

  const documented = live.filter((item) => entryDocumentCount(item) > 0).length;
  const entries = live.length === 1 ? "1 entry" : `${String(live.length)} entries`;

  return documented === 0 ? entries : `${entries} · ${String(documented)} with documents`;
}

/**
 * Live verifications, whether they are on the registry or waiting to be.
 *
 * One helper so the collapsed row and the timeline cannot disagree — there is a
 * test that asserts they agree, and it would otherwise pass while both were
 * wrong in the same way.
 */
function verifiedCount(item: ProvenanceItem): number {
  if (item.kind === "recorded") return item.entry.verifiedByCount;
  return item.entry.acts.stance === "verify" ? 1 : 0;
}

function isLive(item: ProvenanceItem): boolean {
  return item.kind === "draft" || item.entry.endedAt === null;
}

function entryId(item: ProvenanceItem): string {
  return item.entry.id;
}

function entryOwner(item: ProvenanceItem): string {
  const headline = entryHeadline(item.entry);
  if (headline !== "") return headline;

  // A draft with nothing typed yet still needs a name on its toggle. A recorded
  // entry cannot reach this — every kind has a required field and the headline
  // is one of them for seven of the eight — but `auction` may honestly have no
  // buyer, so it falls back to its house rather than to nothing.
  return item.kind === "draft" ? "New entry" : HISTORY_KINDS[item.entry.kind].chip;
}

function entryDocumentCount(item: ProvenanceItem): number {
  return item.kind === "draft" ? item.entry.documents.length : item.entry.documentCount;
}

/**
 * '2003', '1979–2001', '1979–Present', 'Date unknown'.
 *
 * A point kind never renders a range and never renders '–Present'. A sale ran
 * from a date to a date, but an auction HAPPENED on one — and '2018–Present'
 * under a lot number would be the card asserting that the sale is still going
 * on.
 */
function entryDates(entry: DraftEntry | ProvenanceEntry): string {
  const start = "startDate" in entry ? entry.startDate : (entry.startText ?? "");
  const end = "startDate" in entry ? entry.endDate : (entry.endText ?? "");

  if (HISTORY_KINDS[kindOf(entry)].dates === "point") {
    return start.trim() === "" ? PROVENANCE_COPY.dateUnknown : start.trim();
  }

  return describeDateRange(start, end);
}

function toHeader(
  item: ProvenanceItem,
  index: number,
  total: number,
): EntryHeaderModel {
  const ownerName = entryOwner(item);

  return {
    index: index + 1,
    label: `Entry ${String(index + 1)} of ${String(total)}: ${ownerName}`,
    ownerName,
  };
}

/**
 * The card at the top of one link.
 *
 * `detail` is where the work was and who handled it; `notes` is the author's own
 * sentence and is kept separate rather than concatenated, because one is the
 * record's structured fields and the other is somebody talking.
 */
/**
 * THE VERB COMES OFF THE METHOD ON A SALE, OFF THE ACT ON A WATCHED LOAN, AND
 * OFF THE KIND EVERYWHERE ELSE.
 *
 * This is the whole of what makes eight chips enough. The design offers no chip
 * for a gift, a bequest, an inheritance or a restitution — they are Sale with a
 * method — and if the card read its verb off the kind, a bequest would appear on
 * the timeline as 'Sale', which is a different claim about how a family came by
 * a picture. Reading the verb off `METHOD_EVENTS` means the chip is the way in
 * and the record is what it says.
 *
 * A LOAN IS THE SECOND SUCH KIND, since OND-9. A loan's life writes up to three
 * entries — agreed, received, returned — and all three are kind `loan`, so
 * reading the verb off the kind would print 'Loaned' three times. That is not
 * three claims; it is one claim repeated, and a confirmation on any of them
 * would mean nothing in particular. `loanAct` is null on a loan entry somebody
 * typed, which falls through to the kind's own verb and should.
 *
 * NO THIRD BRANCH IS POSSIBLE: `loan_act` is refused on every kind but `loan`
 * by a check constraint, and `method` on every kind but `sale`, so the two
 * conditions cannot both hold.
 */
function toCard(item: ProvenanceItem): EntryCardModel {
  const entry: DraftEntry | ProvenanceEntry = item.entry;
  const model = HISTORY_KINDS[kindOf(entry)];
  const event = entry.kind === "sale" && entry.method ? METHOD_EVENTS[entry.method] : null;
  // `in` rather than a cast: a DraftEntry is a form in progress and carries no
  // act, because only a completed act writes one.
  const act =
    entry.kind === "loan" && "loanAct" in entry && entry.loanAct
      ? LOAN_ACT_EVENTS[entry.loanAct]
      : null;
  const grade = item.kind === "draft" ? item.entry.grade : item.entry.evidenceClaim;
  const notes = ("notes" in entry ? entry.notes : null) ?? "";

  return {
    action: event?.action ?? act?.action ?? model.action,
    typeLabel: (
      event ? methodLabel(entry.method) : (act?.badge ?? model.badge)
    ).toUpperCase(),
    // The tone is the KIND's on a loan act. The tones group what can happen to a
    // work and carry no judgement, and an arrival is not a different sort of
    // event from an agreement — only a different sentence about one.
    tone: event?.tone ?? model.tone,
    glyph: event?.glyph ?? act?.glyph ?? model.glyph,
    dates: entryDates(entry),
    ownerName: entryOwner(item),
    detail: entryDetail(entry),
    notes: notes.trim() === "" ? null : notes.trim(),
    pill: gradePill(grade),
    documentCount: countDocuments(entryDocumentCount(item)),
    strip: confirmationStrip(item),
  };
}

function methodLabel(method: ProvenanceMethod | null): string {
  return ACQUISITION_METHODS.find((option) => option.key === method)?.label ?? "Not known";
}

/**
 * The read view of an entry, field by field, in the kind's own words.
 *
 * Built from the same table the form was, so what a collector typed under
 * 'Venue / museum' reads back under 'Venue / museum' — and a reader is never
 * shown 'Acquisition method: —' on an exhibition, which is a row saying nothing
 * about a question nobody asked.
 */
function toFieldRows(entry: ProvenanceEntry): FieldRow[] {
  const model = HISTORY_KINDS[entry.kind];
  const rows: FieldRow[] = [{ label: "Type", value: model.chip }];

  for (const field of model.fields) {
    if (field.control === "text") {
      rows.push({ label: strip(field.label), value: textOf(entry, field.key) || "—" });
    } else if (field.control === "method") {
      rows.push({ label: strip(field.label), value: methodLabel(entry.method) });
    } else if (field.control === "money") {
      // Rendered with its currency or not at all. A bare '1,850,000' on a
      // record read by somebody in another country is worse than a dash.
      rows.push({
        label: strip(field.label),
        value:
          entry.insuredMinor === null || entry.insuredCurrency === null
            ? "—"
            : formatMoney({ minor: entry.insuredMinor, currency: entry.insuredCurrency }),
      });
    } else {
      rows.push({
        label: strip(field.label),
        value: entry.documentKind ? documentKindLabel(entry.documentKind) : "—",
      });
    }
  }

  rows.push(
    { label: model.dates === "point" ? model.dateLabels.start : "Dates", value: entryDates(entry) },
    { label: strip(model.notes.label), value: entry.notes ?? "—" },
    { label: "Recorded by", value: entry.authorName ?? "—" },
  );

  return rows;
}

function toOndoLine(ondoId: string | null): OndoLine | null {
  return ondoId === null ? null : { ondoId, note: PROVENANCE_COPY.onOndo };
}

function documentKindLabel(kind: ProvenanceDocumentKind): string {
  return DOCUMENT_KINDS.find((option) => option.key === kind)?.label ?? "Document";
}

/**
 * What the record holds about one paper, and what this reader may do to it.
 *
 * `canJudge` is passed in rather than read off the document, because it is a
 * SURFACE question and the surfaces may only take away: the register wizard
 * composes drafts that are not on the registry, so there is nothing there to
 * accept or reject. What decides it on the surfaces that do offer it is the
 * server's own answer about the entry — `canAttest` is
 * `attestable_provenance_entry_ids()`, which is "anyone who can read it", and
 * the document's gate is the same read one level down.
 */
export function documentVerdictFor(
  document: ProvenanceDocument,
  canJudge: boolean,
): DocumentVerdictModel {
  const accepted = document.verifiedByCount;
  const rejected = document.disputedByCount;
  const said = [
    accepted > 0 ? `${PROVENANCE_COPY.acceptedBy} ${String(accepted)}` : null,
    rejected > 0 ? `${PROVENANCE_COPY.rejectedBy} ${String(rejected)}` : null,
  ].filter(isDefined);

  return {
    acceptedBy: accepted,
    rejectedBy: rejected,
    mine: document.myVerdict,
    mineId: document.myVerdictId,
    // Empty rather than 'Accepted by 0': a paper nobody has judged should read
    // as nothing said, not as a score.
    summary: said.join(" · "),
    canJudge,
  };
}

/**
 * Whether a verdict on a paper can be sent, and the sentence saying why not.
 *
 * ONE RULE, SAID WHERE THE FIELD IS STILL OPEN.
 * `provenance_document_verdicts_dispute_reasoned` refuses a reasonless
 * rejection and arrives as a 422 after the composer has closed, taking the
 * words with it — so this is checked first, and the composer keeps the text
 * when it returns a sentence.
 *
 * It is NOT a second authority. The database still decides; this only avoids
 * sending something it has already said it will refuse, which is the same
 * division `askBody` keeps for an entry's dispute.
 */
export function verdictRefusal(
  stance: "verify" | "dispute",
  reason: string,
): string | null {
  if (stance === "verify") return null;
  return reason.trim() === "" ? PROVENANCE_COPY.verdictNeedsReason : null;
}

/**
 * How an entry is contested, in words — or null when nothing contests it.
 *
 * THE PRECEDENCE IS NOT DECIDED HERE. The server sends a `standing`, because
 * whether a rejected paper outranks a confirmation is a product rule and it
 * lives in `ondo.provenance_entry_standing()`. This turns the answer into a
 * pill and a sentence and does no comparing of its own — the one thing it adds
 * is the document note, and only on the standing the documents caused.
 */
export function standingFor(entry: ProvenanceEntry): StandingModel | null {
  if (!isContested(entry.standing)) return null;

  const pill = STANDING_PILLS[entry.standing];
  const rejected = entry.disputedDocumentCount;

  return {
    label: pill.pill,
    sentence: pill.sentence,
    token: pill.token,
    bgToken: pill.bgToken,
    // Only where the papers are what put it here. A disputed entry may also
    // carry rejected documents, and saying so beside DISPUTED would offer the
    // weaker fact as the explanation for the stronger one.
    documentNote:
      // `plural` carries the number itself — "1 document", "2 documents" — which
      // is why the count is not written in front of it here.
      entry.standing === "questionable" && rejected > 0
        ? `${plural(rejected, PROVENANCE_COPY.rejectedDocuments)} ${PROVENANCE_COPY.rejectedSuffix}`
        : null,
  };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
